/** Checking settings layers and freeze windows from the API before they are stored. */
import { FREEZE_BLOCKS, IDLE_CLOSE_MAX_MINUTES, SECRET_NAME_RE, isAgentKindToken } from '@orgus/protocol';
import type { FreezeBlock, FreezeWindowInput, LandingReviewer, PolicyScope, PolicySettings, PolicySettingsInput } from '@orgus/protocol';
import { isReservedEnvName } from './env-names.ts';
import { parseClock } from './freeze.ts';
import { createRedactor } from './redact.ts';
import { PERMISSION_MODES } from './settings.ts';
import type { Validated } from './validate.ts';

const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);
const MODEL_RE = /^[A-Za-z0-9._:[\]*?-]{1,100}$/;
const ENV_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;
const CHECK_RE = /^[A-Za-z0-9._:/ -]{1,60}$/;
const KIND_RE = /^[a-z][a-z0-9-]{1,39}$/;
const REVIEWERS: readonly LandingReviewer[] = ['none', 'human', 'agent', 'human-or-agent'];
/** Credentials Claude Code reads: written to a settings file they would sit on disk in clear (lease them as secrets). */
const CREDENTIAL_ENV = /^(ANTHROPIC_API_KEY|ANTHROPIC_AUTH_TOKEN|CLAUDE_CODE_OAUTH_TOKEN)$/i;

function scopeOf(x: unknown): Validated<PolicyScope> {
  if (!isObj(x) || (x.kind !== 'org' && x.kind !== 'project')) return { ok: false, error: 'Pick a scope: every project or one project.' };
  if (x.kind === 'project') {
    if (typeof x.projectId !== 'string' || !x.projectId) return { ok: false, error: 'A project setting needs a project.' };
    return { ok: true, value: { kind: 'project', projectId: x.projectId } };
  }
  return { ok: true, value: { kind: 'org' } };
}

function strList(x: unknown, name: string, re: RegExp, max = 50): Validated<string[]> {
  if (!Array.isArray(x)) return { ok: false, error: `${name} must be a list.` };
  const out: string[] = [];
  for (const v of x) {
    if (typeof v !== 'string' || !re.test(v.trim())) return { ok: false, error: `${name}: "${String(v)}" is not valid.` };
    if (!out.includes(v.trim())) out.push(v.trim());
  }
  if (out.length > max) return { ok: false, error: `${name} can have at most ${max} entries.` };
  return { ok: true, value: out };
}

const looksSecret = createRedactor({ patterns: true });

/** One layer of settings. Unset or null fields are left out (they inherit). */
export function validatePolicySettings(x: unknown): Validated<PolicySettings> {
  if (!isObj(x)) return { ok: false, error: 'Settings must be an object.' };
  const out: PolicySettings = {};
  const set = (k: string) => x[k] !== undefined && x[k] !== null && x[k] !== '';
  if (set('defaultModel')) {
    if (typeof x.defaultModel !== 'string' || !MODEL_RE.test(x.defaultModel) || /[*?]/.test(x.defaultModel)) return { ok: false, error: 'The default model must be one model id or alias, such as sonnet or claude-haiku-4-5.' };
    out.defaultModel = x.defaultModel;
  }
  if (set('allowedModels')) {
    const v = strList(x.allowedModels, 'Allowed models', MODEL_RE);
    if (!v.ok) return v;
    out.allowedModels = v.value;
  }
  if (set('permissionMode')) {
    if (!(PERMISSION_MODES as readonly unknown[]).includes(x.permissionMode)) return { ok: false, error: `The permission mode must be one of ${PERMISSION_MODES.join(', ')}.` };
    out.permissionMode = x.permissionMode as string;
  }
  if (set('disableBypassPermissions')) {
    if (typeof x.disableBypassPermissions !== 'boolean') return { ok: false, error: 'Block bypass permissions must be true or false.' };
    out.disableBypassPermissions = x.disableBypassPermissions;
  }
  if (set('autonomy')) {
    if (x.autonomy !== 'full' && x.autonomy !== 'ask') return { ok: false, error: 'Autonomy must be full or ask.' };
    out.autonomy = x.autonomy;
  }
  if (set('secrets')) {
    const v = strList(x.secrets, 'Secrets', SECRET_NAME_RE);
    if (!v.ok) return { ok: false, error: `${v.error} Secret names are environment variable names: capitals, digits and underscores.` };
    const reserved = v.value.find(isReservedEnvName);
    if (reserved) return { ok: false, error: `${reserved} is reserved: it changes how Orgus, the machine or Claude Code runs.` };
    out.secrets = v.value;
  }
  if (set('env')) {
    if (!isObj(x.env)) return { ok: false, error: 'Environment must be a map of NAME to value.' };
    const env: Record<string, string> = {};
    for (const [k, v] of Object.entries(x.env)) {
      if (!ENV_NAME_RE.test(k)) return { ok: false, error: `"${k}" is not a valid environment variable name.` };
      if (isReservedEnvName(k)) return { ok: false, error: `${k} is reserved: it changes how Orgus, the machine or Claude Code runs.` };
      if (CREDENTIAL_ENV.test(k)) return { ok: false, error: `${k} is a credential. Environment values are written to the session's settings file; store it under Secrets and lease it instead.` };
      if (typeof v !== 'string' || v.length > 4096) return { ok: false, error: `The value of ${k} must be text of at most 4096 characters.` };
      if (looksSecret.text(v) !== v) return { ok: false, error: `The value of ${k} looks like a secret. Environment values are written to the session's settings file; store it under Secrets and lease it instead.` };
      env[k] = v;
    }
    if (Object.keys(env).length > 100) return { ok: false, error: 'At most 100 environment variables.' };
    out.env = env;
  }
  if (set('requiredChecks')) {
    const v = strList(x.requiredChecks, 'Required checks', CHECK_RE, 20);
    if (!v.ok) return v;
    out.requiredChecks = v.value;
  }
  if (set('allowedAgentKinds')) {
    const v = strList(x.allowedAgentKinds, 'Allowed agent kinds', KIND_RE, 40);
    if (!v.ok) return { ok: false, error: `${v.error} An agent kind is a token such as claude-code, codex-acp or gemini-cli.` };
    if (!v.value.every(isAgentKindToken)) return { ok: false, error: 'Allowed agent kinds: use agent kind tokens such as claude-code or gemini-cli.' };
    if (!v.value.length) return { ok: false, error: 'Allowed agent kinds cannot be empty: leave it unset to allow every kind.' };
    out.allowedAgentKinds = v.value;
  }
  if (set('idleCloseMinutes')) {
    const v = x.idleCloseMinutes;
    if (typeof v !== 'number' || !Number.isInteger(v) || v < 0 || v > IDLE_CLOSE_MAX_MINUTES) {
      return { ok: false, error: `Close idle sessions after: whole minutes from 0 (never) to ${IDLE_CLOSE_MAX_MINUTES}.` };
    }
    out.idleCloseMinutes = v;
  }
  if (set('reviewer')) {
    if (!REVIEWERS.includes(x.reviewer as LandingReviewer)) return { ok: false, error: `The reviewer must be one of ${REVIEWERS.join(', ')}.` };
    out.reviewer = x.reviewer as LandingReviewer;
  }
  return { ok: true, value: out };
}

export function validateSettingsInput(x: unknown): Validated<PolicySettingsInput> {
  if (!isObj(x)) return { ok: false, error: 'Send { scope, settings }.' };
  const scope = scopeOf(x.scope);
  if (!scope.ok) return scope;
  const settings = validatePolicySettings(x.settings ?? {});
  if (!settings.ok) return settings;
  return { ok: true, value: { scope: scope.value, settings: settings.value } };
}

export function validateFreezeInput(x: unknown): Validated<FreezeWindowInput> {
  if (!isObj(x)) return { ok: false, error: 'A freeze window must be an object.' };
  const name = typeof x.name === 'string' ? x.name.trim() : '';
  if (!name || name.length > 80) return { ok: false, error: 'Name the freeze window (at most 80 characters).' };
  const scope = scopeOf(x.scope);
  if (!scope.ok) return scope;
  const blocks = Array.isArray(x.blocks) ? (x.blocks.filter((b) => (FREEZE_BLOCKS as readonly unknown[]).includes(b)) as FreezeBlock[]) : [];
  if (!blocks.length || (Array.isArray(x.blocks) && blocks.length !== x.blocks.length)) return { ok: false, error: `Say what the window stops: ${FREEZE_BLOCKS.join(', ')}.` };
  const reason = typeof x.reason === 'string' && x.reason.trim() ? x.reason.trim().slice(0, 300) : undefined;
  if (x.enabled !== undefined && typeof x.enabled !== 'boolean') return { ok: false, error: 'Enabled must be true or false.' };
  const base = { name, scope: scope.value, blocks: [...new Set(blocks)], ...(reason ? { reason } : {}), ...(x.enabled === false ? { enabled: false } : {}) };
  if (x.kind === 'once') {
    const from = Number(x.from);
    const to = Number(x.to);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return { ok: false, error: 'A one-off window needs a start and an end after it.' };
    return { ok: true, value: { ...base, kind: 'once', from, to } };
  }
  if (x.kind === 'weekly') {
    const days = Array.isArray(x.days) ? [...new Set(x.days.map(Number))].filter((d) => Number.isInteger(d) && d >= 0 && d <= 6).sort() : [];
    if (!days.length) return { ok: false, error: 'Pick at least one day for a weekly window.' };
    const start = typeof x.start === 'string' ? x.start.trim() : '';
    const end = typeof x.end === 'string' ? x.end.trim() : '';
    if (parseClock(start) == null || parseClock(end) == null) return { ok: false, error: 'Weekly times are HH:MM, 24-hour, for example 22:00.' };
    if (start === end) return { ok: false, error: 'The end time must differ from the start time.' };
    const off = x.utcOffsetMinutes === undefined ? 0 : Number(x.utcOffsetMinutes);
    if (!Number.isInteger(off) || Math.abs(off) > 14 * 60) return { ok: false, error: 'The time zone offset is not valid.' };
    return { ok: true, value: { ...base, kind: 'weekly', days, start, end, utcOffsetMinutes: off } };
  }
  return { ok: false, error: 'A freeze window is once (from and to) or weekly (days and times).' };
}
